import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { MovieContext } from "../context/MovieContext";
import TMDBSearch from "../components/TMDBSearch";

const AddMovie = () => {
  const { addMovie } = useContext(MovieContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    description: "",
    genre: "",
    year: "",
    poster: "",
    posterFile: null,
    trailer: "",
    rating: 3.0,
  });

  const [showTMDBSearch, setShowTMDBSearch] = useState(false);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const genres = [
    "Action", "Comedy", "Drama", "Horror", "Sci-Fi",
    "Romance", "Thriller", "Animation", "Documentary", "Fantasy"
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!form.title.trim()) newErrors.title = "Title is required";
    if (!form.genre.trim()) newErrors.genre = "Genre is required";
    if (!form.year || isNaN(form.year) || form.year < 1900 || form.year > 2030) {
      newErrors.year = "Enter a valid year";
    }
    if (!form.poster.trim()) newErrors.poster = "Poster URL is required";
    if (!form.description.trim()) newErrors.description = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file' && files && files[0]) {
      const file = files[0];
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, poster: "File size must be less than 5MB" }));
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, poster: "Please upload an image file" }));
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm((prev) => ({ 
          ...prev, 
          poster: reader.result,
          posterFile: file 
        }));
      };
      reader.readAsDataURL(file);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, poster: "File size must be less than 5MB" }));
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, poster: "Please upload an image file" }));
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm((prev) => ({ 
          ...prev, 
          poster: reader.result,
          posterFile: file 
        }));
      };
      reader.readAsDataURL(file);
    }

    if (errors.poster) {
      setErrors((prev) => ({ ...prev, poster: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const newMovie = {
        ...form,
        year: parseInt(form.year),
        rating: parseFloat(form.rating),
        id: Date.now(),
      };

      addMovie(newMovie);

      setForm({
        title: "",
        description: "",
        genre: "",
        year: "",
        poster: "",
        trailer: "",
        rating: 3,
      });

      setTimeout(() => navigate("/movies"), 800);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950">
      {/* HEADER */}
      <div className="bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white py-12 lg:py-16 shadow-2xl relative overflow-hidden">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-transparent to-pink-500/10 animate-pulse"></div>
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '100px 100px'}}></div>
        
        {/* Radial Glow */}
        <div className="absolute right-[5%] top-1/2 -translate-y-1/2 w-[500px] h-[600px] bg-gradient-radial from-orange-500/20 via-orange-500/5 to-transparent blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
                Add New Movie
              </span>
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Share your favorite movies with our community
            </p>
          </div>
        </div>
      </div>

      {/* FORM SECTION */}
      <div className="container mx-auto px-4 py-8 lg:py-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-white/10">
            
            

            {/* FORM BODY */}
            <form onSubmit={handleSubmit} className="p-6 lg:p-10 space-y-8">
              
              {/* MOVIE SEARCH BUTTON */}
              <div className="bg-gradient-to-r from-orange-500/10 to-pink-500/10 rounded-2xl p-6 border border-orange-500/20">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/25">
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-lg">Import Movie Data</h3>
                      <p className="text-sm text-gray-400">Search and extract movie data automatically</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTMDBSearch(true)}
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 whitespace-nowrap"
                  >
                    Search Movies
                  </button>
                </div>
              </div>

              {/* TITLE FIELD */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-gray-300">
                  Movie Title <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <input
                    name="title"
                    type="text"
                    value={form.title}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none transition-all text-white placeholder-gray-400"
                    placeholder="Enter movie title..."
                  />
                  {errors.title && (
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <span className="text-red-500 font-bold">!</span>
                    </div>
                  )}
                </div>
                {errors.title && <p className="text-sm text-red-400">{errors.title}</p>}
              </div>

              {/* TWO COLUMN FIELDS */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* GENRE FIELD */}
                <div className="space-y-2">
                  <label className="block text-sm font-bold text-gray-300">
                    Genre <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="genre"
                      value={form.genre}
                      onChange={handleChange}
                      className="dark-select w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none transition-all text-white appearance-none cursor-pointer"
                    >
                      <option value="">Select genre...</option>
                      {genres.map((genre) => (
                        <option key={genre} value={genre}>{genre}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 20 20">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 8l4 4-4 4" />
                      </svg>
                    </div>
                  </div>
                  {errors.genre && (
                    <p className="text-sm text-red-400 flex items-center">
                      <span className="mr-2 font-bold">!</span>
                      {errors.genre}
                    </p>
                  )}
                </div>

                {/* YEAR FIELD */}
                <div className="space-y-2">
                  <label className="block text-sm font-bold text-gray-300">
                    Release Year <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      name="year"
                      type="number"
                      value={form.year}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none transition-all text-white placeholder-gray-400"
                      placeholder="e.g., 2023"
                      min="1900"
                      max="2030"
                    />
                    {errors.year && (
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <span className="text-red-500 font-bold">!</span>
                      </div>
                    )}
                  </div>
                  {errors.year && <p className="text-sm text-red-400">{errors.year}</p>}
                </div>
              </div>

              {/* RATING FIELD */}
              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-300">
                  Rating: <span className="text-orange-400 font-bold ml-2 text-base">{Number(form.rating).toFixed(1)}</span> / 5.0
                </label>
                <div className="flex items-center space-x-4 bg-gray-800/50 rounded-xl p-4">
                  <span className="text-sm text-gray-400 font-medium">Poor</span>
                  <input
                    name="rating"
                    type="range"
                    min="0"
                    max="5"
                    step="0.1"
                    value={form.rating}
                    onChange={handleChange}
                    className="flex-1 h-2 bg-gradient-to-r from-orange-200 via-orange-400 to-pink-500 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-sm text-gray-400 font-medium">Excellent</span>
                </div>
              </div>
              {/* POSTER FIELD */}
              <div className="space-y-3">
                <label className="block text-sm font-bold text-gray-300">
                  Movie Poster <span className="text-red-400">*</span>
                </label>
                
                {/* UPLOAD OPTION */}
                <div className="mb-4">
                  <div className="relative">
                    <input
                      type="file"
                      name="posterFile"
                      accept="image/*"
                      onChange={handleChange}
                      className="hidden"
                      id="poster-upload"
                    />
                    <label 
                      htmlFor="poster-upload"
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`flex items-center justify-center w-full px-4 py-8 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 ${
                        isDragging 
                          ? 'border-orange-500 bg-orange-500/10 shadow-lg shadow-orange-500/20' 
                          : 'border-white/20 hover:border-orange-400 hover:bg-orange-500/5'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                          isDragging ? 'bg-orange-500/20' : 'bg-white/10'
                        }`}>
                          <span className={`text-2xl transition-colors ${isDragging ? 'text-orange-400' : 'text-gray-400'}`}>📷</span>
                        </div>
                        <span className={`text-sm font-semibold transition-colors ${isDragging ? 'text-orange-300' : 'text-gray-300'}`}>
                          {isDragging ? 'Drop image here' : 'Click to upload image'}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">or drag and drop (max 5MB)</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* OR DIVIDER */}
                <div className="relative flex items-center justify-center mb-4">
                  <div className="border-t border-white/10 w-full"></div>
                  <span className="bg-gray-900/50 px-4 text-sm text-gray-400 font-medium">OR</span>
                  <div className="border-t border-white/10 w-full"></div>
                </div>

                {/* URL INPUT */}
                <div className="relative">
                  <input
                    name="poster"
                    type="url"
                    value={form.poster}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none transition-all text-white placeholder-gray-400"
                    placeholder="Enter image URL..."
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-400">🔗</span>
                  </div>
                  {errors.poster && (
                    <div className="absolute inset-y-0 right-10 flex items-center pr-3">
                      <span className="text-red-500 font-bold">!</span>
                    </div>
                  )}
                </div>
                {errors.poster && <p className="text-sm text-red-400">{errors.poster}</p>}
                
                {/* POSTER PREVIEW */}
                {form.poster && (
                  <div className="mt-4 p-4 bg-gradient-to-br from-orange-500/10 to-white/5 rounded-xl border border-orange-500/20">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-bold text-gray-300 flex items-center">
                        <span className="mr-2">✓</span>
                        Poster Preview
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setForm(prev => ({ ...prev, poster: '', posterFile: null }));
                          document.getElementById('poster-upload').value = '';
                        }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <span className="text-xl">×</span>
                      </button>
                    </div>
                    <div className="flex justify-center">
                      <img
                        src={form.poster}
                        alt="Movie poster"
                        className="h-48 w-auto rounded-xl shadow-lg object-cover border border-gray-200"
                      />
                    </div>
                    {form.posterFile && (
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        {form.posterFile.name} ({(form.posterFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* TRAILER FIELD */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-gray-300">
                  Trailer URL
                  <span className="text-xs text-gray-500 ml-2 font-normal">(Optional)</span>
                </label>
                <div className="relative">
                  <input
                    name="trailer"
                    type="url"
                    value={form.trailer}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none transition-all text-white placeholder-gray-400"
                    placeholder="https://www.youtube.com/embed/..."
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className="text-gray-400">🎬</span>
                  </div>
                </div>
              </div>

              {/* DESCRIPTION FIELD */}
              <div className="space-y-2">
                <label className="block text-sm font-bold text-gray-300">
                  Description <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <textarea
                    name="description"
                    rows={4}
                    value={form.description}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 outline-none transition-all text-white placeholder-gray-400 resize-none"
                    placeholder="Write a brief description of the movie..."
                  />
                  <div className="absolute bottom-3 right-3 flex items-center justify-between">
                    <span className="text-xs text-gray-500">{form.description.length}/500</span>
                  </div>
                </div>
                {errors.description && (
                  <p className="text-sm text-red-400 flex items-center">
                    <span className="mr-2 font-bold">!</span>
                    {errors.description}
                  </p>
                )}
              </div>

              {/* FORM ACTIONS */}
              <div className="flex flex-col sm:flex-row gap-4 pt-8 border-t border-white/10">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold rounded-xl px-6 py-4 transition-all duration-300 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Adding Movie...</span>
                    </>
                  ) : (
                    <>
                      <span>+</span>
                      <span>Add Movie</span>
                    </>
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate("/movies")}
                  className="flex-1 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white font-semibold rounded-xl px-6 py-4 transition-all duration-300 flex items-center justify-center space-x-2 border border-white/20"
                >
                  <span>×</span>
                  <span>Cancel</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* TMDB Search Modal */}
      {showTMDBSearch && (
        <TMDBSearch
          onSelectMovie={(movieData) => {
            setForm(prev => ({
              ...prev,
              ...movieData,
              posterFile: null
            }));
            setShowTMDBSearch(false);
          }}
          onClose={() => setShowTMDBSearch(false)}
        />
      )}
    </div>
  );
};

export default AddMovie;